import { findProviderData } from '@app/component/provider/FindProviderData';
import ReviewOutput from '@app/model/review/output/ReviewOutput';
import { BackspaceIcon, DocumentDuplicateIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import { ReviewService } from '@app/model/review/service/ReviewService';
import ReviewStateState from '@app/model/review/ReviewStateState';
import useToaster from '@app/component/overlays/toaster/ToasterHook';

const ReviewMessageReplyFragment: React.FunctionComponent<{ reviewOutput: ReviewOutput }> = ({ reviewOutput }) => {

	const tools = useReviewsTools();
	const queryClient = useQueryClient();
	const [answer, setAnswer] = React.useState<string>('');
	const answerRef = React.useRef<HTMLTextAreaElement | null>(null);
	const toaster = useToaster();
	const [isReplyGenerated, setIsReplyGenerated] = React.useState<boolean>(false);

	useEffect(() => {
		setAnswer('');
		setIsReplyGenerated(false);
	}, [reviewOutput]);

	const markReviewAsAnswered = () => {
		ReviewService.markReviewAsAnswered(reviewOutput.review.id, tools.filter, queryClient);
	};

	const markReviewAsUnanswered = () => {
		ReviewService.markReviewAsUnanswered(reviewOutput.review.id, tools.filter, queryClient);
	};

	const copyCommentToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(answer ?? '')
				.then(() => toaster.success('Review Management', 'Comment copied to clipboard'))
				.catch(() => toaster.error('Review Management', 'Failed to copy comment to clipboard'));
		} catch (e) {
			const el = answerRef.current;
			if (!el) return;
			el.focus();
			el.select();
			try {
				document.execCommand('copy');
			} finally {
				el.setSelectionRange(el.value.length, el.value.length);
			}
		}
	};

	const clearComment = () => {
		setAnswer('');
		answerRef.current?.focus();
	};

	const generateReply = () => {
		setAnswer('Auto-generated reply')
		setIsReplyGenerated(true);
	}

	return <div className="bg-white px-4 py-6 shadow sm:rounded-lg sm:px-6">
		<form>
			<div>
				<div className="flex items-center" aria-orientation="horizontal" role="tablist">
					<div className="flex items-center space-x-2"/>

					<div className="ml-auto flex items-center space-x-1">
						<button type="button"
										onClick={clearComment}
										className="-m-2.5 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:text-gray-500">
							<BackspaceIcon className="h-5 w-5" />
						</button>
						<button type="button"
										onClick={copyCommentToClipboard}
										className="-m-2.5 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:text-gray-500">
							<DocumentDuplicateIcon className="h-5 w-5" />
						</button>
					</div>
				</div>
				<div className="mt-2">
					<div className="-m-0.5 rounded-lg p-0.5">
						<label htmlFor="answer" className="sr-only">Comment</label>
						<textarea ref={answerRef} rows={8} name="answer" id="answer" className="input"
											value={answer}
											onChange={(e) => setAnswer(e.target.value)}
											placeholder="Add your answer..."></textarea>
					</div>
				</div>
				{isReplyGenerated && <div className="mt-3">
					<div className="flex justify-between">
						<label htmlFor="input-normal" className="label">Do you want to change anything at this response?</label>
					</div>
					<div className="mt-0.5">
						<input type="text" name="input-normal" id="input-normal" className="input" placeholder="Insert AI promt" />
					</div>
				</div>}
			</div>
			<div className="flex justify-between flex-wrap gap-x-3 gap-y-2 mt-5">
				<div className="flex items-center flex-wrap gap-x-3 gap-y-2">
					<button type="button" className="btn btn-white text-blue-500" onClick={generateReply}>
						<SparklesIcon className="-ml-1 mr-1 h-4 w-4" />
						Generate Reply
					</button>
				</div>
				<div className="flex items-center flex-wrap gap-x-3 gap-y-2">
					{reviewOutput.session.state === ReviewStateState.ANSWERED ? <button type="button" className="btn btn-white" onClick={markReviewAsUnanswered}>Mark as Unanswered</button> : <button type="button" className="btn btn-white" onClick={markReviewAsAnswered}>Mark as Answered</button>}
					<button className="btn btn-white">Copy & Open</button>
					<button className="btn btn-primary">
						<span className="-ml-1 mr-2 h-5 w-auto">
							{findProviderData(reviewOutput.review.provider).icon}
						</span>
						<span>Reply</span>
						<ChevronRightIcon className="-mr-1 ml-1 h-4 w-4" />
					</button>
				</div>
			</div>
		</form>
	</div>;
};

export default ReviewMessageReplyFragment;